import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { supabase, BACKEND_URL } from '../../supabaseClient';
import { 
  Shield, 
  Key, 
  Mail, 
  Bell, 
  Trash2, 
  AlertTriangle, 
  CheckCircle, 
  X, 
  Lock, 
  Smartphone 
} from 'lucide-react';
import './SecuritySettings.css'; // ★ NEW CSS FILE

const SecuritySettings = ({ session }) => {
  // --- STATE MANAGEMENT (Logic Preserved) ---
  const [searchParams, setSearchParams] = useSearchParams();
  const provider = session?.user?.app_metadata?.provider;
  
  // 1. Password State
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // 2. Backup Email State
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupEmailInput, setBackupEmailInput] = useState('');
  const [backupOtp, setBackupOtp] = useState('');
  const [backupStep, setBackupStep] = useState(1); 
  const [isBackupLoading, setIsBackupLoading] = useState(false);
  const [existingBackupEmail, setExistingBackupEmail] = useState(null);
  const [backupResendTimer, setBackupResendTimer] = useState(0);

  // 3. Communication Preferences State
  const [isEditingPrefs, setIsEditingPrefs] = useState(false);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [commPrefs, setCommPrefs] = useState({
    marketing: true,
    tech_updates: true,
    surveys: false,
    security_alerts: true 
  });

  // 4. Deletion Wizard State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); 
  const [deleteOtp, setDeleteOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [otherReasonText, setOtherReasonText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [retentionDiscount, setRetentionDiscount] = useState('50'); 
  const [resendTimer, setResendTimer] = useState(0); 
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // --- EFFECTS ---
  useEffect(() => {
    if (searchParams.get('email_change_success') === 'true') {
      toast.success("Primary email updated successfully!", { icon: '🔐' });
      searchParams.delete('email_change_success');
      setSearchParams(searchParams);
    }

    const fetchProfileData = async () => {
        const { data } = await supabase
            .from('user_metadata')
            .select('backup_email, comm_preferences')
            .eq('user_id', session.user.id)
            .maybeSingle();
        
        if (data) {
            if (data.backup_email) setExistingBackupEmail(data.backup_email);
            if (data.comm_preferences) {
                setCommPrefs(prev => ({ ...prev, ...data.comm_preferences }));
            }
        }
    };
    fetchProfileData();
  }, [searchParams, setSearchParams, session.user.id]);

  // Timers
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    let interval;
    if (backupResendTimer > 0) {
      interval = setInterval(() => setBackupResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [backupResendTimer]);

  // Fetch Coupon
  useEffect(() => {
    if (showDeleteModal) {
       const fetchCoupon = async () => {
          try {
             const res = await fetch(`${BACKEND_URL}/api/retention/coupon?t=${Date.now()}`, {
                headers: { 'Authorization': `Bearer ${session.access_token}` }
             });
             const data = await res.json();
             if (data.discount) setRetentionDiscount(data.discount);
          } catch (e) { console.error("Failed to fetch coupon"); }
       };
       fetchCoupon();
    }
  }, [showDeleteModal, session.access_token]);


  // --- HELPERS ---
  const handleToggleComm = (key) => {
    if (key === 'security_alerts') return;
    setCommPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveCommPrefs = async () => {
      setIsSavingPrefs(true);
      try {
          const response = await fetch(`${BACKEND_URL}/api/profile`, {
              method: 'PUT',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ comm_preferences: commPrefs })
          });

          if (!response.ok) throw new Error("Failed to save preferences");
          toast.success("Preferences saved successfully");
          setIsEditingPrefs(false);
      } catch (error) {
          toast.error("Could not save settings");
      } finally {
          setIsSavingPrefs(false);
      }
  };

  // --- HANDLERS: PASSWORD ---
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) return toast.error("Passwords do not match");
    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success("Password updated successfully");
      setIsPasswordOpen(false);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // --- HANDLERS: BACKUP EMAIL ---
  const openBackupModal = () => {
      setBackupStep(1);
      setBackupEmailInput('');
      setBackupOtp('');
      setShowBackupModal(true);
  };

  const handleSendBackupOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(backupEmailInput)) return toast.error("Please enter a valid email address");
    if (backupResendTimer > 0) return;

    setIsBackupLoading(true);
    try {
        const response = await fetch(`${BACKEND_URL}/api/security/backup-email/request`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ email: backupEmailInput })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Failed to send code");

        toast.success(`Verification code sent to ${backupEmailInput}`);
        setBackupStep(2);
        setBackupResendTimer(60);
    } catch (err) {
        toast.error(err.message);
    } finally {
        setIsBackupLoading(false);
    }
  };

  const handleVerifyBackupOtp = async () => {
    if (backupOtp.length !== 6) return toast.error("Enter 6-digit code");
    setIsBackupLoading(true);
    try {
        const response = await fetch(`${BACKEND_URL}/api/security/backup-email/verify`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ email: backupEmailInput, otp: backupOtp })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Verification failed");

        setBackupStep(3);
        setExistingBackupEmail(backupEmailInput);
        toast.success("Backup email verified & secured!");
        
        setTimeout(() => setShowBackupModal(false), 2000);
    } catch (err) {
        toast.error(err.message);
    } finally {
        setIsBackupLoading(false);
    }
  };

  // --- HANDLERS: DELETION ---
  const handleSendDeleteOtp = async () => {
    if (resendTimer > 0) return; 
    try {
      const response = await fetch(`${BACKEND_URL}/api/send-deletion-otp`, {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
         }
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to send code");

      setOtpSent(true);
      setResendTimer(60); 
      toast.success("OTP sent to your primary email");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const verifyDeleteOtpAndProceed = async () => {
    if (deleteOtp.length !== 6) return toast.error("Enter a 6-digit code");
    setIsVerifyingOtp(true);
    try {
       const response = await fetch(`${BACKEND_URL}/api/verify-deletion-otp`, {
          method: 'POST',
          headers: {
             'Content-Type': 'application/json',
             'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ otp: deleteOtp })
       });
       if (!response.ok) throw new Error("Invalid Verification Code");
       setDeleteStep(2); 
    } catch (error) {
       toast.error(error.message);
    } finally {
       setIsVerifyingOtp(false);
    }
  };

  const submitSurveyAndProceed = () => {
    if (!deleteReason) return toast.error("Please select a reason");
    setDeleteStep(3); 
  };

  const handleClaimOffer = async () => {
     try {
        toast.loading("Applying offer...");
        const response = await fetch(`${BACKEND_URL}/api/retention/claim-offer`, {
           method: 'POST',
           headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
           },
           body: JSON.stringify({ discount: retentionDiscount })
        });
        if (!response.ok) throw new Error("Failed to send offer");
        toast.dismiss();
        toast.success(`Coupon sent! Redirecting...`);
        setTimeout(() => setShowDeleteModal(false), 1500);
     } catch (error) {
        toast.dismiss();
        toast.error("Could not claim offer.");
     }
  };

  const handleFinalDeletion = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/delete-account-with-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ otp: deleteOtp, reason: deleteReason, feedback: otherReasonText })
      });
      if (!response.ok) throw new Error("Deletion failed");
      toast.success("Account scheduled for deletion. Goodbye!");
      setTimeout(() => supabase.auth.signOut(), 2000);
    } catch (err) {
      toast.error(err.message);
      setIsDeleting(false);
    }
  };

  return (
    <div className="security-page-container fade-in">
      <Toaster position="bottom-right" />

      {/* HEADER */}
      <div className="security-banner">
        <div>
          <h1>Security & Privacy</h1>
          <p>Manage your account security and communication preferences.</p>
        </div>
        <div className="security-icon-circle">
            <Shield size={32} />
        </div>
      </div>

      {/* --- CARD 1: LOGIN DETAILS --- */}
      <div className="account-card">
        <div className="account-card-header">
          <div className="header-icon-row">
             <Key className="header-icon" size={24} />
             <h2>Login Details</h2>
          </div>
          <p>Manage your primary access credentials</p>
        </div>
        
        {provider === 'email' ? (
          <div className="account-card-content">
            <div className="email-status-row">
               <div className="email-info">
                 <label>PRIMARY EMAIL</label>
                 <div className="email-value">{session?.user.email}</div>
               </div>
               <span className="verified-badge"><CheckCircle size={14} /> Verified</span>
            </div>

            {!isPasswordOpen ? (
               <div className="password-row-closed">
                  <div className="password-meta">
                    <div className="meta-title">Password</div>
                    <div className="meta-subtitle">Last changed: Recently</div>
                  </div>
                  <button className="btn btn-secondary" onClick={() => setIsPasswordOpen(true)}>Change Password</button>
               </div>
            ) : (
              <form onSubmit={handlePasswordUpdate} className="password-form">
                  <div className="form-grid-2col">
                     <div className="input-group">
                        <label className="theme-label">New Password</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" required className="input-field"/>
                     </div>
                     <div className="input-group">
                        <label className="theme-label">Confirm Password</label>
                        <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="Repeat password" required className="input-field"/>
                     </div>
                  </div>
                  <div className="form-actions-right">
                     <button type="button" className="btn btn-secondary" onClick={() => setIsPasswordOpen(false)}>Cancel</button>
                     <button type="submit" className="btn btn-primary-theme" disabled={isUpdatingPassword}>
                        {isUpdatingPassword ? "Saving..." : "Update Password"}
                     </button>
                  </div>
              </form>
            )}
          </div>
        ) : (
          <div className="account-card-content">
             <div className="provider-alert">
                <Lock size={18} />
                <span>You are logged in via <strong>{provider}</strong>. Please manage your security through that provider.</span>
             </div>
          </div>
        )}
      </div>

      {/* --- CARD 2: BACKUP SECURITY --- */}
      <div className="account-card">
        <div className="account-card-header">
          <div className="header-icon-row">
             <Shield className="header-icon" size={24} />
             <h2>Backup Security</h2>
          </div>
          <p>Add a recovery email to restore access if you lose your primary credentials.</p>
        </div>
        <div className="account-card-content">
           <div className="backup-row">
              <div className="backup-info">
                 <div className="backup-title">Recovery Email</div>
                 <div className="backup-status">
                    {existingBackupEmail ? (
                        <>
                            <span className="email-text">{existingBackupEmail}</span>
                            <span className="verified-badge"><CheckCircle size={12} /> Active</span>
                        </>
                    ) : (
                        <span className="text-muted">No recovery email set</span>
                    )}
                 </div>
              </div>
              <button className="btn btn-secondary" onClick={openBackupModal}>
                 {existingBackupEmail ? 'Change' : 'Add Recovery Email'}
              </button>
           </div>
        </div>
      </div>

      {/* --- CARD 3: COMMUNICATION PREFERENCES --- */}
      <div className="account-card">
        <div className="account-card-header">
          <div className="header-icon-row">
             <Bell className="header-icon" size={24} />
             <h2>Communication Preferences</h2>
          </div>
          <p>Control which emails you receive from us.</p>
        </div>
        <div className="account-card-content">
           <div className="prefs-list">
               {/* Marketing */}
               <div className="pref-item">
                  <div className="pref-info">
                     <div className="pref-title">Marketing & Offers</div>
                     <div className="pref-desc">Receive updates on discounts, seasonal deals, and promotions.</div>
                  </div>
                  <label className="toggle-switch">
                     <input type="checkbox" checked={commPrefs.marketing} onChange={() => handleToggleComm('marketing')} disabled={!isEditingPrefs} />
                     <span className="slider"></span>
                  </label>
               </div>
               
               {/* Tech Updates */}
               <div className="pref-item">
                  <div className="pref-info">
                     <div className="pref-title">Product & Tech Updates</div>
                     <div className="pref-desc">New engine features, changelogs, and tool upgrades.</div>
                  </div>
                  <label className="toggle-switch">
                     <input type="checkbox" checked={commPrefs.tech_updates} onChange={() => handleToggleComm('tech_updates')} disabled={!isEditingPrefs} />
                     <span className="slider"></span>
                  </label>
               </div>

               {/* Surveys */}
               <div className="pref-item">
                  <div className="pref-info">
                     <div className="pref-title">User Surveys</div>
                     <div className="pref-desc">Feedback requests to help us improve the platform.</div>
                  </div>
                  <label className="toggle-switch">
                     <input type="checkbox" checked={commPrefs.surveys} onChange={() => handleToggleComm('surveys')} disabled={!isEditingPrefs} />
                     <span className="slider"></span>
                  </label>
               </div>

               {/* Security (Locked) */}
               <div className="pref-item locked">
                  <div className="pref-info">
                     <div className="pref-title">Security Alerts</div>
                     <div className="pref-desc">Login alerts, password resets, and OTPs. (Required)</div>
                  </div>
                  <Lock size={16} className="lock-icon" />
               </div>
           </div>

           <div className="account-card-footer">
              {isEditingPrefs ? (
                 <div className="form-actions">
                    <button className="btn btn-secondary" onClick={() => setIsEditingPrefs(false)}>Cancel</button>
                    <button className="btn btn-primary-theme" onClick={handleSaveCommPrefs} disabled={isSavingPrefs}>
                       {isSavingPrefs ? "Saving..." : "Save Changes"}
                    </button>
                 </div>
              ) : (
                 <button className="btn btn-secondary" onClick={() => setIsEditingPrefs(true)}>Edit Preferences</button>
              )}
           </div>
        </div>
      </div>

      {/* --- CARD 4: DANGER ZONE --- */}
      <div className="account-card danger-card">
        <div className="account-card-header">
          <div className="header-icon-row danger-text">
             <AlertTriangle className="header-icon" size={24} />
             <h2>Danger Zone</h2>
          </div>
        </div>
        <div className="account-card-content danger-content">
           <div className="danger-info">
              <strong>Close Account</strong>
              <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
           </div>
           <button className="btn btn-outline-danger" onClick={() => { setShowDeleteModal(true); setDeleteStep(1); setOtpSent(false); setDeleteOtp(''); }}>
              <Trash2 size={16} /> Close Account
           </button>
        </div>
      </div>

      {/* --- MODALS (Reused Logic, Updated UI) --- */}
      
      {/* 1. BACKUP MODAL */}
      {showBackupModal && (
         <div className="modal-overlay">
            <div className="modal-card">
               <div className="modal-header">
                  <h3>Add Recovery Email</h3>
                  <button onClick={() => setShowBackupModal(false)} className="close-modal-btn"><X size={24} /></button>
               </div>
               <div className="modal-body">
                  {backupStep === 1 && (
                     <div className="fade-in">
                        <p className="modal-text">Enter the email address you want to use for account recovery.</p>
                        <div className="input-group">
                           <label className="theme-label">Recovery Email</label>
                           <input type="email" value={backupEmailInput} onChange={e => setBackupEmailInput(e.target.value)} placeholder="e.g. backup@gmail.com" className="input-field" />
                        </div>
                        <button className="btn btn-primary-theme full-width mt-4" onClick={handleSendBackupOtp} disabled={isBackupLoading}>
                           {isBackupLoading ? "Sending Code..." : "Send Verification Code"}
                        </button>
                     </div>
                  )}
                  {backupStep === 2 && (
                     <div className="fade-in center-text">
                        <Mail size={48} className="modal-icon neon-text" />
                        <h4>Check your inbox</h4>
                        <p className="modal-text">We sent a code to <strong>{backupEmailInput}</strong></p>
                        <input type="text" value={backupOtp} onChange={e => setBackupOtp(e.target.value)} placeholder="123456" maxLength={6} className="otp-input" />
                        <button className="btn btn-primary-theme full-width mt-4" onClick={handleVerifyBackupOtp} disabled={isBackupLoading}>
                           {isBackupLoading ? "Verifying..." : "Verify & Save"}
                        </button>
                        <div className="modal-links">
                           <button onClick={() => setBackupStep(1)} className="text-link">Use different email</button>
                           <button onClick={handleSendBackupOtp} disabled={backupResendTimer > 0} className="text-link">
                              {backupResendTimer > 0 ? `Resend in ${backupResendTimer}s` : "Resend Code"}
                           </button>
                        </div>
                     </div>
                  )}
                  {backupStep === 3 && (
                     <div className="fade-in center-text">
                        <CheckCircle size={64} className="success-icon" />
                        <h3 className="success-text">Secured!</h3>
                        <p className="modal-text">Your recovery email has been verified and saved.</p>
                     </div>
                  )}
               </div>
            </div>
         </div>
      )}

      {/* 2. DELETE MODAL */}
      {showDeleteModal && (
         <div className="modal-overlay">
            <div className="modal-card">
               <div className="modal-header">
                  <h3 className="danger-text">Delete Account</h3>
                  <button onClick={() => setShowDeleteModal(false)} className="close-modal-btn"><X size={24} /></button>
               </div>
               <div className="modal-body">
                  {deleteStep === 1 && (
                     <div className="fade-in center-text">
                        <Shield size={48} className="modal-icon" />
                        <h4>Security Verification</h4>
                        <p className="modal-text">For your security, please verify your identity before proceeding.</p>
                        {!otpSent ? (
                           <button className="btn btn-primary-theme full-width" onClick={handleSendDeleteOtp}>Send Verification Code</button>
                        ) : (
                           <>
                              <input type="text" value={deleteOtp} onChange={e => setDeleteOtp(e.target.value)} placeholder="123456" maxLength={6} className="otp-input" />
                              <button className="btn btn-outline-danger full-width mt-4" onClick={verifyDeleteOtpAndProceed} disabled={deleteOtp.length !== 6 || isVerifyingOtp}>
                                 {isVerifyingOtp ? "Verifying..." : "Verify & Continue"}
                              </button>
                           </>
                        )}
                     </div>
                  )}
                  {/* Step 2 (Survey), 3 (Retention), 4 (Final) follow similar patterns structure */}
                  {/* Simplified logic for brevity, structure matches Step 1 style */}
                  {deleteStep === 2 && (
                      <div className="fade-in">
                          <h4>We're sorry to see you go.</h4>
                          <div className="survey-options">
                              {['expensive', 'engine', 'ui', 'duplicate', 'other'].map(reason => (
                                  <label key={reason} className={`survey-option ${deleteReason === reason ? 'selected' : ''}`}>
                                      <input type="radio" name="deleteReason" value={reason} checked={deleteReason === reason} onChange={(e) => setDeleteReason(e.target.value)} />
                                      {reason === 'expensive' && "Too expensive / Pricing"}
                                      {reason === 'engine' && "Engine results aren't good enough"}
                                      {reason === 'ui' && "UI is confusing"}
                                      {reason === 'duplicate' && "I have another account"}
                                      {reason === 'other' && "Other"}
                                  </label>
                              ))}
                          </div>
                          <button className="btn btn-primary-theme full-width" onClick={submitSurveyAndProceed}>Next</button>
                      </div>
                  )}
                  {deleteStep === 3 && (
                      <div className="fade-in center-text">
                          <div className="gift-icon">🎁</div>
                          <h3>Wait! Don't go yet.</h3>
                          <p className="modal-text">Here is a <strong>{retentionDiscount}% OFF Coupon</strong>.</p>
                          <div className="coupon-box">STAY{retentionDiscount}</div>
                          <button className="btn btn-primary-theme full-width" onClick={handleClaimOffer}>Claim Offer & Stay</button>
                          <button className="text-link mt-2" onClick={() => setDeleteStep(4)}>No thanks, delete account</button>
                      </div>
                  )}
                  {deleteStep === 4 && (
                      <div className="fade-in center-text">
                          <div className="bye-icon">👋</div>
                          <h3>We will miss you.</h3>
                          <p className="modal-text">Permanently delete in 24 hours?</p>
                          <button className="btn btn-outline-danger full-width" onClick={handleFinalDeletion} disabled={isDeleting}>
                              {isDeleting ? 'Deleting...' : 'Confirm Permanent Deletion'}
                          </button>
                      </div>
                  )}
               </div>
            </div>
         </div>
      )}
    </div>
  );
};

export default SecuritySettings;